// @flow
// Labels are clickable, but users can always just click the radio button.
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import { Theme, withTheme } from '@material-ui/core/styles';
import React, { useState } from 'react';

import { withTranslation } from 'react-i18next';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import RadioGroup from '@material-ui/core/RadioGroup';
import { ListItemIcon, List, Collapse } from '@material-ui/core';
import Radio from '@material-ui/core/Radio';
import Toggle from 'react-toggled';
import ExpandLessOutlinedIcon from '@material-ui/icons/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@material-ui/icons/ExpandMoreOutlined';
import { connect, useSelector } from 'react-redux';

import GroupSettingsIcon from '../../../assets/icons/group-settings.svg';
import GroupSettingsIconWhite from '../../../assets/icons/group-settings-white.svg';
import ReadOnlyIcon from '../../../assets/icons/read-only.svg';
import CustomGroupIcon from '../../../assets/icons/custom-group.svg';

import { getSocketIfExist } from '../../utils/getSocket';
import { sendConversationModeUpdateMessage } from '../../utils/ws-v1';

import { selectUser } from '../../selectors/user';

type Props = {
  t: Function,
  theme: Theme,
  subscription: Object,
  avaId: string,
  conversationMode: Object,
  hostAvaId: string,
  isInConversation: Boolean,
};

const ConversationModeSelector = React.memo(function ConversationModeSelector(props: Props) {
  const { t, theme, subscription, conversationMode, hostAvaId, avaId, isInConversation } = props;
  const [selectedMode, setSelectedMode] = useState(undefined);
  const [selectTimeout, setSelectTimeout] = useState(undefined);
  const user = useSelector(selectUser);
  const handleChange = conversationMode => {
    sendConversationModeUpdateMessage(getSocketIfExist(), conversationMode, !!user?.features?.saveTranscript);

    // If a new mode was selected in the last 5 seconds, show it to create
    // a quick transition. After 5 seconds let's return to what the backend
    // returns to show user the true state.
    setSelectedMode(conversationMode);
    if (selectTimeout) {
      clearTimeout(selectTimeout);
      setSelectTimeout(undefined);
    }
    setSelectTimeout(
      setTimeout(() => {
        setSelectedMode(undefined);
      }, 5000)
    );
  };

  if (!isInConversation) {
    return null;
  }

  const modeToShow = selectedMode || conversationMode;

  const isHost = avaId === hostAvaId;
  const sufficientSubscription =
    subscription &&
    subscription.ongoingSubscription &&
    subscription.ongoingSubscription !== 'Free' &&
    subscription.ongoingSubscription !== 'Freedom' &&
    subscription.ongoingSubscriptionSubtype !== 'Lite';

  return isHost && sufficientSubscription ? (
    <Toggle>
      {({ on, getTogglerProps }) => [
        <ListItem button {...getTogglerProps()} key="header">
          <ListItemIcon>
            <img
              src={theme.palette.type === 'dark' ? GroupSettingsIconWhite : GroupSettingsIcon}
              alt="Conversation Settings Icon"
            />
          </ListItemIcon>
          <ListItemText>{t('conversation.conversationMode.name')}</ListItemText>

          <ListItemIcon style={{ marginRight: 0, minWidth: 0 }}>
            {on ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
          </ListItemIcon>
        </ListItem>,
        <Collapse in={on} key="toggle_collapse">
          <List style={{ paddingLeft: 10 }}>
            <ListItem>
              <RadioGroup aria-label="conversation-mode" name="mode" value={modeToShow}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => handleChange('public')}
                  >
                    <Radio size="small" style={{ color: theme.palette.ava.blue }} value="public" />
                    <ListItemIcon style={{ minWidth: 24 }}>
                      <img src={CustomGroupIcon} alt="Interactive Mode Icon" />
                    </ListItemIcon>
                    <ListItemText>{t('conversation.conversationMode.interactiveMode')}</ListItemText>
                  </div>
                  <ListItemText
                    style={{ marginLeft: 10, marginTop: -10 }}
                    secondary={t('conversation.conversationMode.interactiveDesc')}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => handleChange('public-muted')}
                  >
                    <Radio size="small" style={{ color: theme.palette.ava.blue }} value="public-muted" />
                    <ListItemIcon style={{ minWidth: 24 }}>
                      <img src={ReadOnlyIcon} alt="Read Only Icon" />
                    </ListItemIcon>
                    <ListItemText>{t('conversation.conversationMode.readOnlyMode')}</ListItemText>
                  </div>
                  <ListItemText
                    style={{ marginLeft: 10, marginTop: -10 }}
                    secondary={t('conversation.conversationMode.readOnlyDesc')}
                  />
                </div>
              </RadioGroup>
            </ListItem>
          </List>
        </Collapse>,
      ]}
    </Toggle>
  ) : null;
});

const mapStateToProps = ({
  scribeConversation: { isInConversation, status },
  scribeSession: { avaId, subscription },
}) => ({
  avaId,
  isInConversation,
  subscription,
  conversationMode: status.conversationMode,
  hostAvaId: status.host?.avaId,
});

export default connect(mapStateToProps, {})(withTheme(withTranslation()(ConversationModeSelector)));
